// Dip-sites map layer — converts the in-memory dipSiteStore list into
// a GeoJSON FeatureCollection MapLibre can render as point markers.
//
// Color by ownership_class:
//   federal  → green   (no reimbursement worries — go ahead and pump)
//   private  → amber   (reimbursement may apply; pilot should still
//                       use the source if safe, but note the pull)
//   tribal   → purple  (treat carefully; tribal nations have their
//                       own water-use frameworks)
//   state    → blue    (state land; state cooperator agreement may apply)
//   unknown  → gray    (not yet classified — lookup pending)
//
// All sources share an icon (droplet); color carries the ownership
// hint at a glance.

import type { Feature, FeatureCollection, Point } from 'geojson';
import type { DipSite } from '../lib/dipSites';

export interface DipSiteFeatureProps {
  dipSiteId: string;
  name: string;
  sourceType: string;
  ownershipClass: string;
  managingAgency: string | null;
  ownerName: string | null;
  reimbursementStatus: string;
}

export function buildDipSitesFC(sites: ReadonlyArray<DipSite>): FeatureCollection<Point, DipSiteFeatureProps> {
  const features: Array<Feature<Point, DipSiteFeatureProps>> = [];
  for (const s of sites) {
    if (s.latitude == null || s.longitude == null) continue;
    if (!Number.isFinite(s.latitude) || !Number.isFinite(s.longitude)) continue;
    features.push({
      type: 'Feature',
      properties: {
        dipSiteId:           s.id,
        name:                s.name,
        sourceType:          s.sourceType,
        ownershipClass:      s.ownershipClass,
        managingAgency:      s.managingAgency,
        ownerName:           s.ownerName,
        reimbursementStatus: s.reimbursementStatus,
      },
      geometry: { type: 'Point', coordinates: [s.longitude, s.latitude] },
    });
  }
  return { type: 'FeatureCollection', features };
}

/** Color mapping for the ownership_class field — used as a MapLibre
 *  data-driven expression `match` lookup. */
export const DIPSITE_OWNERSHIP_COLOR_EXPR = [
  'match', ['get', 'ownershipClass'],
  'federal', '#22c55e',   // green
  'private', '#f59e0b',   // amber
  'tribal',  '#a855f7',   // purple
  'state',   '#3b82f6',   // blue
  /* unknown / default */ '#9ca3af', // gray
] as const;
